import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import {
  useSubscriptionPlans,
  useSubscriptionStatuses,
  useUsers,
} from '@features/users/model/use-users';
import {
  describePlanOption,
  humanizeStatus,
  NO_SUBSCRIPTION,
  type PlatformUser,
} from '@features/users/model/user.types';
import { AssignPlanDialog } from '@features/users/ui/AssignPlanDialog';
import { UserDetailDialog } from '@features/users/ui/UserDetailDialog';
import { UsersTable } from '@features/users/ui/UsersTable';
import { Spinner } from '@shared/ui/Spinner';
import { MultiSelect, type MultiSelectOption } from '@shared/ui/MultiSelect';
import { useDebouncedValue } from '@shared/hooks/use-debounced-value';
import { cn } from '@shared/lib/cn';
import type { ApiError } from '@shared/api/api-error';

const PAGE_SIZE = 300;

export function UsersPage() {
  // Filters live in the URL, so a filtered list can be bookmarked and shared,
  // and the back button behaves the way people expect.
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get('page') ?? 1) || 1);
  const urlSearch = searchParams.get('q') ?? '';
  const statusParam = searchParams.get('status') ?? '';

  const planParam = searchParams.get('plan') ?? '';

  const statuses = useMemo(
    () => statusParam.split(',').map((entry) => entry.trim()).filter(Boolean),
    [statusParam],
  );

  const planIds = useMemo(
    () => planParam.split(',').map((entry) => entry.trim()).filter(Boolean),
    [planParam],
  );

  const [searchInput, setSearchInput] = useState(urlSearch);
  const debouncedSearch = useDebouncedValue(searchInput.trim(), 350);

  // The user whose plan is being assigned; null closes the dialog.
  const [assigning, setAssigning] = useState<PlatformUser | null>(null);
  // The user whose record is open; null closes the dialog.
  const [opened, setOpened] = useState<PlatformUser | null>(null);

  // Keep the box in step when the URL changes from outside (back button, a link).
  useEffect(() => {
    setSearchInput(urlSearch);
  }, [urlSearch]);

  const updateParams = (next: Record<string, string>) => {
    setSearchParams(
      (current) => {
        const merged = new URLSearchParams(current);

        for (const [key, value] of Object.entries(next)) {
          if (value) {
            merged.set(key, value);
          } else {
            merged.delete(key);
          }
        }

        return merged;
      },
      { replace: true },
    );
  };

  // Typing drives the URL once it settles; page resets because the current page
  // may not exist in the new result set.
  useEffect(() => {
    if (debouncedSearch !== urlSearch) {
      updateParams({ q: debouncedSearch, page: '' });
    }
    // `urlSearch` is intentionally excluded: reacting to it here would fight the
    // sync above and loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const params = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      ...(urlSearch ? { search: urlSearch } : {}),
      ...(statuses.length ? { status: statuses.join(',') } : {}),
      ...(planIds.length ? { plan: planIds.join(',') } : {}),
    }),
    [page, urlSearch, statuses, planIds],
  );

  const { data, isPending, isFetching, isError, error } = useUsers(params);
  const availableStatuses = useSubscriptionStatuses();
  const availablePlans = useSubscriptionPlans();

  const statusOptions: MultiSelectOption[] = [
    ...(availableStatuses.data ?? []).map((value) => ({
      value,
      label: humanizeStatus(value),
    })),
    { value: NO_SUBSCRIPTION, label: 'No subscription' },
  ];

  // Several plans share a name ("Monthly" appears twice), so the id disambiguates
  // them in the URL while the price and cadence tell them apart on screen.
  const planOptions: MultiSelectOption[] = (availablePlans.data ?? []).map((plan) => ({
    value: plan.id,
    label: describePlanOption(plan),
    ...(plan.isActive ? {} : { hint: 'retired' }),
  }));

  const meta = data?.meta;
  // Selecting "no subscription" forces per-page filtering server-side, so the
  // total describes this page rather than the whole result.
  const isPerPageCount = statuses.includes(NO_SUBSCRIPTION);

  const rangeStart = meta ? (meta.page - 1) * meta.pageSize + 1 : 0;
  const rangeEnd = meta ? Math.min(meta.page * meta.pageSize, meta.total) : 0;

  return (
    // `flex-1 min-h-0` rather than `h-full`: a percentage height does not resolve
    // against a flex-sized parent, which is what let the whole page scroll.
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <div className="relative">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search users"
            aria-label="Search users by email, name or organization"
            className="h-9 w-64 rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500"
          />
        </div>

        <MultiSelect
          className="w-44"
          placeholder="All plans"
          values={planIds}
          options={planOptions}
          onChange={(next) => updateParams({ plan: next.join(','), page: '' })}
        />

        <MultiSelect
          className="w-44"
          placeholder="All statuses"
          values={statuses}
          options={statusOptions}
          onChange={(next) => updateParams({ status: next.join(','), page: '' })}
        />

        {/* Reserves its own width so the toolbar does not shift while fetching. */}
        <span className="flex w-4 justify-center">
          {isFetching && !isPending && <Spinner className="h-4 w-4" />}
        </span>
      </div>

      <UsersTable
        users={data?.users ?? []}
        loading={isPending}
        error={isError ? (error as ApiError).message : null}
        onAssignPlan={setAssigning}
        onOpen={setOpened}
      />

      <UserDetailDialog user={opened} onClose={() => setOpened(null)} />

      <AssignPlanDialog user={assigning} onClose={() => setAssigning(null)} />

      {!isPending && data && data.users.length > 0 && (
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
          <p className="text-xs tabular-nums text-slate-500">
            {isPerPageCount
              ? `${data.users.length} on this page`
              : `${rangeStart}–${rangeEnd} of ${meta?.total}`}
          </p>

          <div className="flex items-center gap-1">
            <PageButton
              label="Previous page"
              disabled={page <= 1}
              onClick={() => updateParams({ page: String(page - 1) })}
            >
              <ChevronLeft aria-hidden className="h-4 w-4" />
            </PageButton>

            <span className="px-2 text-xs tabular-nums text-slate-500">
              {meta?.page} / {meta?.totalPages || 1}
            </span>

            <PageButton
              label="Next page"
              disabled={!meta || page >= meta.totalPages}
              onClick={() => updateParams({ page: String(page + 1) })}
            >
              <ChevronRight aria-hidden className="h-4 w-4" />
            </PageButton>
          </div>
        </div>
      )}

      {isPerPageCount && !isPending && (
        <p className="shrink-0 text-xs text-slate-400">
          “No subscription” is applied per page, so the count and page total describe this page
          rather than the whole result.
        </p>
      )}
    </div>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition-colors',
        disabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-slate-50 hover:text-slate-900',
      )}
    >
      {children}
    </button>
  );
}
